import React, { useState, useEffect } from 'react';
import cl from './Materials.module.css';
import AdminService from '../../API/AdminService';
import { useNavigate } from 'react-router-dom';
import logger from '../../utils/logger';
import {
    IconBook, IconRefresh, IconAlert, IconArrowRight,
    IconShieldCheck, IconStudents, IconChart, IconGamepad,
    IconMessage, IconHelp,
} from '../../components/Icons';

const Materials = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        centers: 0,
        students: 0,
        projects: 0,
        minecraftTasks: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            setError('');

            // Один запрос на бэк вместо четырёх. Эндпоинт /api/v1/admin/stats/dashboard
            // сам сходит к user-сервису за регистрациями и заданиями Майнкрафтии.
            const resp = await AdminService.getDashboardStats();
            const data = (resp && resp.data) || {};

            setStats({
                centers: data.centers || 0,
                students: data.students || 0,
                projects: data.projects || 0,
                minecraftTasks: data.minecraftTasks || 0,
            });
        } catch (err) {
            logger.error('Критическая ошибка загрузки статистики:', err);
            setError(err.message || 'Ошибка загрузки статистики');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.loadingContainer}>
                    <div className={cl.spinner}></div>
                    <p>Загрузка статистики…</p>
                </div>
            </div>
        );
    }

    // Карточки статистики
    const statCards = [
        {
            key: 'centers',
            icon: <IconShieldCheck width={18} height={18}/>,
            label: 'Детских центров в системе',
            value: stats.centers,
            sub: 'школы программирования и робототехники',
            accent: true,
        },
        {
            key: 'students',
            icon: <IconStudents width={18} height={18}/>,
            label: 'Занимается детей',
            value: stats.students,
            sub: 'зарегистрированных аккаунтов',
        },
        {
            key: 'projects',
            icon: <IconChart width={18} height={18}/>,
            label: 'Выполнено проектов',
            value: stats.projects,
            sub: 'РобоТехника',
        },
        {
            key: 'minecraftTasks',
            icon: <IconGamepad width={18} height={18}/>,
            label: 'Заданий Майнкрафтии',
            value: stats.minecraftTasks,
            sub: 'решено по всей платформе',
        },
    ];

    // Карточки навигации
    const navCards = [
        {
            key: 'forum',
            to: '/main/forum',
            icon: <IconMessage width={22} height={22}/>,
            title: 'Форум',
            desc: 'Общение и вопросы между владельцами центров — обмен опытом, поиск решений, обсуждение новых фич.',
            accent: 'blue',
        },
        {
            key: 'faq',
            to: '/main/faq',
            icon: <IconHelp width={22} height={22}/>,
            title: 'F.A.Q.',
            desc: 'Обучение работе с системой РобоТехники и Майнкрафтии: как настроить курсы, выдавать награды, проверять проекты.',
            accent: 'green',
        },
        {
            key: 'articles',
            to: '/main/articles',
            icon: <IconBook width={22} height={22}/>,
            title: 'Статьи',
            desc: 'Материалы по открытию центров, ведению занятий, маркетингу и развитию — пишите свои и читайте чужие.',
            accent: 'gold',
        },
    ];

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Шапка-карточка */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconBook width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Материалы и сообщество</h1>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Материалы · Сообщество центров России
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderActions}>
                        <button
                            onClick={loadStats}
                            className={cl.btnGhostV2}
                            title="Обновить"
                        >
                            <IconRefresh width={15} height={15}/>
                        </button>
                    </div>
                </div>

                {/* Hero-описание */}
                <div className={cl.heroCard}>
                    <div className={cl.heroBlob1}/>
                    <div className={cl.heroBlob2}/>
                    <div className={cl.heroContent}>
                        <h2 className={cl.heroTitle}>
                            Сообщество центров робототехники и программирования
                        </h2>
                        <p className={cl.heroDesc}>
                            Полезные материалы по детским центрам, общение с другими владельцами
                            и подробное обучение работе в системе. Пишите свои статьи, задавайте
                            вопросы на форуме или предлагайте новые фичи — всё это можно делать здесь.
                        </p>
                    </div>
                </div>

                {/* Ошибка */}
                {error && (
                    <div className={cl.errorToast}>
                        <span className={cl.errorToastIcon}>
                            <IconAlert width={16} height={16}/>
                        </span>
                        <span className={cl.errorToastMsg}>{error}</span>
                        <button onClick={loadStats} className={cl.retryButton}>
                            <IconRefresh width={13} height={13}/>
                            <span>Повторить</span>
                        </button>
                    </div>
                )}

                {/* KPI */}
                <div className={cl.statsContainer}>
                    {statCards.map(c => (
                        <div
                            key={c.key}
                            className={`${cl.statCard} ${c.accent ? cl.statCardAccent : ''}`}
                        >
                            <div className={cl.statTop}>
                                <span className={cl.statIcon}>{c.icon}</span>
                                <div className={cl.statLabel}>{c.label}</div>
                            </div>
                            <div className={cl.statNumber}>{c.value.toLocaleString('ru-RU')}</div>
                            <div className={cl.statSubLabel}>{c.sub}</div>
                        </div>
                    ))}
                </div>

                {/* Навигация */}
                <div className={cl.navSectionTitle}>Куда дальше</div>
                <div className={cl.navigationContainer}>
                    {navCards.map(c => (
                        <button
                            key={c.key}
                            className={`${cl.navCard} ${cl[`navCard_${c.accent}`]}`}
                            onClick={() => navigate(c.to)}
                            type="button"
                        >
                            <div className={cl.navCardIcon}>
                                {c.icon}
                            </div>
                            <div className={cl.navCardBody}>
                                <h3 className={cl.navCardTitle}>{c.title}</h3>
                                <p className={cl.navCardDesc}>{c.desc}</p>
                            </div>
                            <div className={cl.navCardArrow}>
                                <IconArrowRight width={16} height={16}/>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Materials;
